import { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Globe, Smartphone, Cpu, ArrowUpRight, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    icon: Globe,
    title: 'Web Development',
    description: 'Full-stack web applications built with modern frameworks. From responsive landing pages to complex enterprise platforms.',
    features: ['React & Next.js', 'Node.js & Python', 'Cloud Deployment', 'Performance Optimized'],
    image: 'https://images.pexels.com/photos/34803987/pexels-photo-34803987.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    accentBg: 'bg-blue-500/10',
    accentText: 'text-blue-400',
  },
  {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications that deliver seamless experiences on iOS and Android.',
    features: ['React Native', 'Flutter', 'iOS & Android', 'App Store Launch'],
    image: 'https://images.pexels.com/photos/27487608/pexels-photo-27487608.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    accentBg: 'bg-cyan-500/10',
    accentText: 'text-cyan-400',
  },
  {
    icon: Cpu,
    title: 'Custom Software',
    description: 'Tailored software solutions designed to address your unique business challenges and automate workflows.',
    features: ['Enterprise Solutions', 'API Integration', 'Database Design', 'DevOps & CI/CD'],
    image: 'https://images.pexels.com/photos/28428592/pexels-photo-28428592.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    accentBg: 'bg-indigo-500/10',
    accentText: 'text-indigo-400',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 60, rotateX: 10 },
  visible: (i) => ({
    opacity: 1, y: 0, rotateX: 0,
    transition: { duration: 0.7, delay: i * 0.2, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const navigate = useNavigate();
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: parallaxRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '8%']);

  return (
    <section id="services" ref={parallaxRef} data-testid="services-section" className="relative py-24 md:py-32 overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 bg-slate-950" />
      {/* Floating orbs */}
      <motion.div
        animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-20 right-20 w-64 h-64 bg-blue-500/5 rounded-full blur-[100px]"
      />
      <motion.div
        animate={{ y: [0, 15, 0], x: [0, -10, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-20 left-10 w-48 h-48 bg-cyan-500/5 rounded-full blur-[80px]"
      />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
            >
              What We Do
            </motion.span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white mb-4">
              Services We Offer
            </h2>
            <p className="text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
              End-to-end digital solutions crafted with precision and purpose.
            </p>
          </div>
          <Button
            data-testid="services-explore-more"
            onClick={() => navigate('/services')}
            className="bg-transparent border border-white/10 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/5 rounded-full px-6 h-10 text-sm font-medium transition-all duration-300 group self-start md:self-auto"
          >
            Explore All Services
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ perspective: '1000px' }}>
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              custom={index}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              variants={cardVariants}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              data-testid={`service-card-${index}`}
              className="group relative rounded-2xl overflow-hidden border border-white/5 bg-slate-900/40 backdrop-blur-md transition-all duration-500 hover:border-blue-500/20 hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.15)] cursor-pointer"
              onClick={() => navigate('/services')}
            >
              <div className="relative h-48 overflow-hidden">
                <motion.img
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.7 }}
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
                <div className={`absolute top-4 left-4 p-2.5 rounded-xl ${service.accentBg}`}>
                  <service.icon className={`w-5 h-5 ${service.accentText}`} />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading font-semibold text-xl text-white mb-3 flex items-center gap-2">
                  {service.title}
                  <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-blue-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature) => (
                    <span key={feature} className="text-xs px-3 py-1 rounded-full bg-white/5 text-slate-400 border border-white/5">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
